// Mock data for the skeleton phase — shaped 1:1 like app/04-architektura.md §4.
// Replaced by expo-sqlite (guest data) and the mobile API (linked client) in later phases.

import type {
  Household,
  Policy,
  ProtectionScore,
  Reminder,
  SavingsGoal,
} from '@/types/domain';

export const MOCK_USER = {
  firstName: 'Wojtek',
  vocative: 'Wojtku', // "Dzień dobry, Wojtku"
};

export const MOCK_HOUSEHOLD: Household = {
  id: 'hh-0001',
  name: 'Rodzina Kowalskich',
  monthlyBudgetGr: 640_000, // 6 400 zł
  inviteCode: null,
  members: [
    { id: 'mem-0001', role: 'OWNER', firstName: 'Wojtek', accountId: null, birthYear: 1985 },
    { id: 'mem-0002', role: 'PARTNER', firstName: 'Ania', accountId: null, birthYear: 1988 },
    { id: 'mem-0003', role: 'CHILD', firstName: 'Zosia', accountId: null, birthYear: 2019 },
  ],
  declarations: {
    ownsHome: true,
    hasMortgage: true,
    hasCar: true,
    hasKids: true,
    runsBusiness: false,
  },
  updatedAt: '2026-07-10T08:00:00Z',
};

export const MOCK_POLICIES: Policy[] = [
  {
    id: 'pol-0001',
    source: 'MANUAL',
    systemInsuranceId: null,
    type: 'CAR_OC',
    customName: 'Auto (Toyota RAV4)',
    insurerName: 'PZU',
    policyNumber: 'PZ123456789',
    subject: 'Toyota RAV4, WW 12345',
    premiumGr: 120_000, // 1 200 zł / year
    premiumPeriod: 'ANNUAL',
    startDate: '2025-07-16',
    endDate: '2026-07-15',
    openEnded: false,
    installments: [],
    nextTechnicalExamDate: '2027-03-01',
    documentUris: [],
    createdAt: '2026-07-01T10:00:00Z',
    updatedAt: '2026-07-01T10:00:00Z',
  },
  {
    id: 'pol-0002',
    source: 'SCAN',
    systemInsuranceId: null,
    type: 'HOME',
    customName: 'Dom (Mieszkanie)',
    insurerName: 'Ergo Hestia',
    policyNumber: 'EH987654321',
    subject: 'Mieszkanie, Wieluń',
    premiumGr: 45_000, // 450 zł / year
    premiumPeriod: 'ANNUAL',
    startDate: '2026-01-06',
    endDate: '2027-01-05',
    openEnded: false,
    installments: [],
    nextTechnicalExamDate: null,
    documentUris: [],
    createdAt: '2026-07-01T10:00:00Z',
    updatedAt: '2026-07-01T10:00:00Z',
  },
  {
    id: 'pol-0003',
    source: 'MANUAL',
    systemInsuranceId: null,
    type: 'LIFE',
    customName: 'Życie (Indywidualne)',
    insurerName: 'Allianz',
    policyNumber: 'AL456123789',
    subject: null,
    premiumGr: 15_000, // 150 zł / month
    premiumPeriod: 'MONTHLY',
    startDate: '2024-03-01',
    endDate: null,
    openEnded: true,
    installments: [],
    nextTechnicalExamDate: null,
    documentUris: [],
    createdAt: '2026-07-01T10:00:00Z',
    updatedAt: '2026-07-01T10:00:00Z',
  },
];

export const MOCK_REMINDERS: Reminder[] = [
  {
    id: 'rem-0001',
    policyId: 'pol-0001',
    type: 'OC_END',
    title: 'Koniec OC',
    subtitle: 'Toyota RAV4',
    eventDate: '2026-07-15',
    leadDaysBefore: [30, 14, 7, 1],
    enabled: true,
    lastFiredAt: null,
    updatedAt: '2026-07-01T10:00:00Z',
  },
  {
    id: 'rem-0002',
    policyId: null,
    type: 'INSTALLMENT',
    title: 'Rata kredytu',
    subtitle: 'Hipoteka',
    eventDate: '2026-07-22',
    leadDaysBefore: [7, 1],
    enabled: true,
    lastFiredAt: null,
    updatedAt: '2026-07-01T10:00:00Z',
  },
  {
    id: 'rem-0003',
    policyId: 'pol-0001',
    type: 'TECH_EXAM',
    title: 'Przegląd auta',
    subtitle: 'Toyota RAV4',
    eventDate: '2027-03-01',
    leadDaysBefore: [30, 7],
    enabled: true,
    lastFiredAt: null,
    updatedAt: '2026-07-01T10:00:00Z',
  },
];

// Computed on-device from declarations + active policies (deterministic rules).
export const MOCK_PROTECTION: ProtectionScore = {
  areas: [
    { key: 'CAR', relevant: true, covered: true, policyIds: ['pol-0001'] },
    { key: 'HOME', relevant: true, covered: true, policyIds: ['pol-0002'] },
    { key: 'LIFE', relevant: true, covered: true, policyIds: ['pol-0003'] },
    { key: 'KIDS_NNW', relevant: true, covered: false, policyIds: [] },
    { key: 'HEALTH', relevant: true, covered: false, policyIds: [] },
  ],
  percent: 72,
  computedAt: '2026-07-10T08:00:00Z',
};

// Pre-existing spending this month, so "Zostało: 1 240 zł" matches the Stitch mock
// (6 400 zł budget − 5 160 zł spent).
export const MOCK_SPENT_THIS_MONTH_GR = 516_000;

// Fixed uneven day-to-day pattern so the pace chart looks like real life,
// deterministic for any day of month (no Math.random in render).
const DAILY_WEIGHTS = [3, 1, 2, 6, 1, 2, 4, 1, 3, 2, 5, 1, 2, 3, 1];

/** Cumulative spending per elapsed day (day 1..elapsedDays), last value = totalGr exactly */
export function mockCumulativeSpendGr(elapsedDays: number, totalGr: number): number[] {
  const weights = Array.from({ length: elapsedDays }, (_, i) => DAILY_WEIGHTS[i % DAILY_WEIGHTS.length]);
  const weightSum = weights.reduce((a, b) => a + b, 0);
  let running = 0;
  return weights.map((w, i) => {
    running += w;
    return i === elapsedDays - 1 ? totalGr : Math.round((totalGr * running) / weightSum);
  });
}

// startingGr > 0 by design — the progress ring never starts at 0%.
export const MOCK_GOALS: SavingsGoal[] = [
  {
    id: 'goal-0001',
    householdId: 'hh-0001',
    name: 'Wakacje nad morzem',
    template: 'VACATION',
    targetGr: 800_000, // 8 000 zł
    startingGr: 320_000, // 3 200 zł -> 40%
    photoUri: null,
    targetDate: '2026-08-15',
    createdAt: '2026-06-01T10:00:00Z',
    updatedAt: '2026-07-01T10:00:00Z',
  },
  {
    id: 'goal-0002',
    householdId: 'hh-0001',
    name: 'Poduszka bezpieczeństwa',
    template: 'EMERGENCY_FUND',
    targetGr: 2_000_000, // 20 000 zł
    startingGr: 650_000, // 6 500 zł -> 33%
    photoUri: null,
    targetDate: null,
    createdAt: '2026-05-10T10:00:00Z',
    updatedAt: '2026-07-01T10:00:00Z',
  },
];
